import { Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useGetFamilyGroupListMutation,
	useGetFamilyGroupMergeCombinationMutation,
} from '@/api/family-management/familyApi';
import { FamilyMemberRelationshipDTO } from '@/api/DTO/member-management/admissionRequest';

export interface FamilyMergePlan {
	pairs: FamilyMemberRelationshipDTO[];
	isMerge: boolean;
}

/**
 * CHA-006 §二–§四: loads the pairs to set when `targetMemberId` joins `memberId`'s
 * family. If the target is already in another family group, staff must confirm the
 * merge before merge-combination is called. Resolves `undefined` on an API error or
 * when staff cancel.
 */
export const useFamilyMergePlan = () => {
	const { t } = useTranslation();
	const showModal = useModal();
	const [getMergeCombination] = useGetFamilyGroupMergeCombinationMutation();
	const [getFamilyGroupList] = useGetFamilyGroupListMutation();
	const [isLoading, setIsLoading] = useState(false);

	const confirmMerge = () =>
		new Promise<boolean>((resolve) => {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('family.mergeFamilyGroupTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('family.mergeFamilyGroupText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.next')}
					confirmationButtonProps={{ variant: 'contained' }}
					onConfirm={() => resolve(true)}
					onClose={() => {
						props.onClose();
						resolve(false);
					}}
				/>
			));
		});

	const loadPlan = async (
		memberId: string,
		targetMemberId: string
	): Promise<FamilyMergePlan | undefined> => {
		setIsLoading(true);
		try {
			const targetGroup = await getFamilyGroupList(targetMemberId);
			if (!('data' in targetGroup) || !targetGroup.data?.success) return;
			const isMerge = (targetGroup.data.data || []).length > 0;
			if (isMerge) {
				setIsLoading(false);
				if (!(await confirmMerge())) return;
				setIsLoading(true);
			}
			const combination = await getMergeCombination({
				memberId,
				targetMemberId,
			});
			if (!('data' in combination) || !combination.data?.success) return;
			return { pairs: combination.data.data || [], isMerge };
		} finally {
			setIsLoading(false);
		}
	};

	return { loadPlan, isLoading };
};
